"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Template } from "@/lib/api";
import {
  TEMPLATE_SHELVES,
  TemplateShelfId,
  getTemplateLibraryMeta,
  getTemplatePrimaryShelf,
} from "@/lib/template-library";
import { TemplateCard } from "@/components/templates/template-card";
import { ArrowRight, Layers3, Sparkles } from "lucide-react";


interface PromptLibraryShowcaseProps {
  templates: Template[];
  viewMode?: "grid" | "list";
  onPreview: (template: Template) => void;
  onUse: (template: Template) => void;
}

export function PromptLibraryShowcase({
  templates,
  onPreview,
  onUse,
}: PromptLibraryShowcaseProps) {
  const [selectedShelf, setSelectedShelf] = useState<"all" | TemplateShelfId>("all");

  const groupedShelves = useMemo(() => {
    const groups = new Map<TemplateShelfId, Template[]>();
    for (const shelf of TEMPLATE_SHELVES) {
      groups.set(shelf.id, []);
    }
    for (const template of templates) {
      const shelfId = getTemplatePrimaryShelf(template);
      groups.get(shelfId)?.push(template);
    }
    return groups;
  }, [templates]);

  const featuredTemplates = useMemo(() => {
    const scored = [...templates].sort((left, right) => {
      const leftMeta = getTemplateLibraryMeta(left);
      const rightMeta = getTemplateLibraryMeta(right);
      const leftScore =
        (left.cover_url ? 2 : 0) +
        (leftMeta.isPromptHero ? 2 : 0) +
        (leftMeta.modelLabel ? 1 : 0) +
        (left.description ? 1 : 0);
      const rightScore =
        (right.cover_url ? 2 : 0) +
        (rightMeta.isPromptHero ? 2 : 0) +
        (rightMeta.modelLabel ? 1 : 0) +
        (right.description ? 1 : 0);
      return rightScore - leftScore;
    });
    return scored.slice(0, 6);
  }, [templates]);

  const availableShelves = useMemo(() => {
    return TEMPLATE_SHELVES.filter((shelf) => (groupedShelves.get(shelf.id)?.length || 0) > 0);
  }, [groupedShelves]);

  const selectedShelfTemplates = selectedShelf === "all" ? [] : groupedShelves.get(selectedShelf) || [];
  const selectedShelfMeta =
    selectedShelf === "all"
      ? null
      : TEMPLATE_SHELVES.find((shelf) => shelf.id === selectedShelf) || null;

  return (
    <div className="space-y-6 pb-20">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={selectedShelf === "all" ? "default" : "outline"}
            className="rounded-full"
            onClick={() => setSelectedShelf("all")}
          >
            Все подборки
          </Button>
          {availableShelves.map((shelf) => (
            <Button
              key={shelf.id}
              variant={selectedShelf === shelf.id ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setSelectedShelf(shelf.id)}
            >
              {shelf.label}
              <Badge variant="secondary" className="ml-2 rounded-full px-2">
                {groupedShelves.get(shelf.id)?.length || 0}
              </Badge>
            </Button>
          ))}
        </div>

        {selectedShelfMeta ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <div>
                <h3 className="text-2xl font-semibold tracking-tight">{selectedShelfMeta.label}</h3>
                <p className="text-sm text-muted-foreground">{selectedShelfMeta.description}</p>
              </div>
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Layers3 className="h-4 w-4" />
                {selectedShelfTemplates.length} шабл.
              </div>
            </div>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {selectedShelfTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={onPreview}
                  onUse={onUse}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {availableShelves.map((shelf) => {
              const items = groupedShelves.get(shelf.id) || [];
              if (items.length === 0) return null;
              return (
                <section key={shelf.id} className="space-y-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <h3 className="text-2xl font-semibold tracking-tight">{shelf.label}</h3>
                      <p className="text-sm text-muted-foreground">{shelf.description}</p>
                    </div>
                    <button
                      onClick={() => setSelectedShelf(shelf.id)}
                      className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                    >
                      <span>Все {items.length}</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.slice(0, 8).map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onPreview={onPreview}
                        onUse={onUse}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
